import { useFormikContext, FieldArray } from 'formik';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';

import { RefField } from '@/react/portainer/gitops/RefField';
import { PathSelector } from '@/react/portainer/gitops/ComposePathField/PathSelector';
import { GitFormModel } from '@/react/portainer/gitops/types';
import { AutoUpdateFieldset } from '@/react/portainer/gitops/AutoUpdateFieldset';
import { useGitRepoValidity } from '@/react/portainer/gitops/hooks/useGitRepoValidity';
import { isBE } from '@/react/portainer/feature-flags/feature-flags.service';
import { baseStackWebhookUrl } from '@/portainer/helpers/webhookHelper';
import { useHelmValuesPreview } from '@/react/portainer/gitops/queries/useHelmValuesPreview';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { GitSourceSelector } from '@/react/portainer/gitops/sources/GitSourceSelector';
import { Source } from '@/react/portainer/gitops/sources/types';

import { FormSection } from '@@/form-components/FormSection';
import { FormControl } from '@@/form-components/FormControl';
import { Button, LoadingButton } from '@@/buttons';
import { FormError } from '@@/form-components/FormError';
import { TextTip } from '@@/Tip/TextTip';
import { ExternalLink } from '@@/ExternalLink';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { Checkbox } from '@@/form-components/Checkbox';

import { useGitHelmChartValues } from '../helmChartSourceQueries/useGitHelmChartValues';
import { useGitHelmChartValidation } from '../helmChartSourceQueries/useGitHelmChartValidation';
import { useGitHelmChartInfo } from '../helmChartSourceQueries/useGitHelmChartInfo';
import { HelmValuesInput } from '../components/HelmValuesInput';
import { ManifestPreviewFormSection } from '../components/ManifestPreviewFormSection';
import { GitHelmDryRunPayload } from '../helmReleaseQueries/gitHelmTypes';

import { HelmDeployToSection } from './HelmDeployToSection';
import { HelmInstallFormValues } from './types';

type Props = {
  namespaces: Array<{ label: string; value: string }>;
  setPreviewIsValid: (isValid: boolean) => void;
};

export function GitInstallFormSections({
  namespaces,
  setPreviewIsValid,
}: Props) {
  const environmentId = useEnvironmentId();
  const { values, errors, setFieldValue, setValues, isSubmitting, isValid } =
    useFormikContext<HelmInstallFormValues>();

  const sourceId = values.SourceId;

  // Convert HelmInstallFormValues to GitFormModel for autocomplete components
  const gitModel: GitFormModel = {
    SourceId: sourceId,
    ComposeFilePathInRepository: '', // Not used for Helm
    RepositoryReferenceName: values.RepositoryReferenceName,
    AdditionalFiles: [], // Not used for Helm
    DeployMethod: 'helm',
  };

  const { isValid: isRepoValid } = useGitRepoValidity({
    sourceId,
    enabled: isBE,
  });

  // Use Helm values preview hook (for merging multiple values files)
  const helmValuesPreviewQuery = useHelmValuesPreview({
    valuesFiles: values.HelmValuesFiles || [],
    reference: values.RepositoryReferenceName,
    sourceId,
    enabled:
      isRepoValid &&
      !!sourceId &&
      Array.isArray(values.HelmValuesFiles) &&
      values.HelmValuesFiles.length > 0 &&
      values.HelmValuesFiles.every((f) => f.trim() !== ''),
  });

  // Validate that the chart path contains a valid Helm chart (Chart.yaml exists)
  const chartValidationQuery = useGitHelmChartValidation({
    chartPath: values.HelmChartPath || './',
    reference: values.RepositoryReferenceName,
    sourceId,
    enabled: isRepoValid,
  });

  // Fetch the chart's default values.yaml from the chart root directory (matches helm show values behavior: https://github.com/helm/helm/blob/4877e2d7e99d61dddbc048414b86f114a2c06ca3/pkg/chart/v2/loader/load.go#L105-L110)
  const gitHelmChartValuesQuery = useGitHelmChartValues({
    chartPath: values.HelmChartPath || './',
    reference: values.RepositoryReferenceName,
    sourceId,
    command: 'values',
  });

  // Fetch Chart.yaml to get chart metadata (version, name, etc.)
  const gitHelmChartInfoQuery = useGitHelmChartInfo({
    chartPath: values.HelmChartPath || './',
    reference: values.RepositoryReferenceName,
    sourceId,
    command: 'chart',
  });

  // Build preview payload for manifest preview
  const previewPayload: GitHelmDryRunPayload = useMemo(
    () => ({
      name: values.name || '',
      namespace: values.namespace || '',
      sourceId,
      helmChartPath: values.HelmChartPath || './',
      helmValuesFiles: values.HelmValuesFiles || [],
      repositoryReferenceName: values.RepositoryReferenceName,
    }),
    [values, sourceId]
  );

  return (
    <div>
      <FormSection title="Git repository" className="mb-8">
        <TextTip color="blue" className="mb-3">
          Configure the Git repository where the Helm chart and Helm
          user-defined values files are located.
        </TextTip>
        <GitSourceSelector
          value={sourceId}
          onChange={handleSourceChange}
          error={errors.SourceId as string | undefined}
        />

        <RefField
          value={values.RepositoryReferenceName || ''}
          onChange={(value) => setFieldValue('RepositoryReferenceName', value)}
          sourceId={sourceId}
          error={errors.RepositoryReferenceName}
        />
      </FormSection>

      {isRepoValid && (
        <>
          {values.AutoUpdate && (
            <FormSection title="Automatic updates" className="mb-8">
              <AutoUpdateFieldset
                environmentType="KUBERNETES"
                deployMethod="helm"
                isForcePullVisible={false}
                value={values.AutoUpdate}
                onChange={(autoUpdateValue) =>
                  setFieldValue('AutoUpdate', autoUpdateValue)
                }
                baseWebhookUrl={baseStackWebhookUrl()}
                webhookId={values.webhookId || ''}
                webhooksDocs="/user/kubernetes/applications/webhooks"
              />
            </FormSection>
          )}
          <HelmDeployToSection
            namespace={values.namespace}
            name={values.name}
            namespaces={namespaces}
            errors={{
              namespace: errors.namespace,
              name: errors.name,
            }}
            onNamespaceChange={(value) => setFieldValue('namespace', value)}
            onNameChange={(value) => setFieldValue('name', value)}
          />
          <FormSection title="Helm chart" className="mb-8">
            <TextTip color="blue" className="mb-3">
              Set the path to the Helm chart folder. Only the Git repository can
              be used as a Helm chart source for now. To use an OCI registry or
              Helm repository, use Helm without Git.
            </TextTip>
            <FormControl label="Helm chart source">
              <PortainerSelect
                value={values.RepositoryURL}
                options={[
                  {
                    label: values.RepositoryURL || '(derived from source)',
                    value: values.RepositoryURL,
                  },
                ]}
                disabled
                onChange={() => {}}
                data-cy="helm-chart-source-select"
              />
            </FormControl>
            <FormControl
              label="Helm chart path"
              errors={
                errors.HelmChartPath ||
                (chartValidationQuery.isError
                  ? 'Invalid Helm chart path. Chart.yaml not found in this directory.'
                  : undefined)
              }
              tooltip="Indicate the path to the Helm chart folder from the root of your repository. The folder must contain a Chart.yaml file."
            >
              <PathSelector
                value={values.HelmChartPath || ''}
                onChange={(value) => setFieldValue('HelmChartPath', value)}
                placeholder="e.g. charts/my-app"
                model={gitModel}
                dirOnly // Only show directories for chart path
                allowEmpty
                inputId="helm-chart-path"
                hasError={
                  !!errors.HelmChartPath || chartValidationQuery.isError
                }
              />
              <TextTip color="blue" className="mb-3 mt-3">
                If no path is provided, the root folder of the repository will
                be used.
              </TextTip>
            </FormControl>
          </FormSection>
          <FormSection title="Install options">
            {gitHelmChartInfoQuery.data?.version && (
              <FormControl
                label="Version"
                tooltip="The version of the Helm chart from Chart.yaml. This cannot be changed when deploying from Git."
              >
                <PortainerSelect
                  value={gitHelmChartInfoQuery.data.version}
                  options={[
                    {
                      label: gitHelmChartInfoQuery.data.version,
                      value: gitHelmChartInfoQuery.data.version,
                    },
                  ]}
                  disabled
                  onChange={() => {}}
                  data-cy="helm-chart-version-select"
                />
              </FormControl>
            )}
            {values.AutoUpdate?.RepositoryAutomaticUpdates && (
              <FormControl
                label="Rollback on failure"
                tooltip="Enables automatic rollback on failure during automatic updates. It may increase the time to upgrade."
                inputId="atomic-input"
              >
                <Checkbox
                  id="atomic-input"
                  checked={values.atomic}
                  data-cy="atomic-checkbox"
                  onChange={(e) => setFieldValue('atomic', e.target.checked)}
                />
              </FormControl>
            )}
            <FormControl
              label="Values files"
              tooltip={
                <>
                  <p>
                    Specify user-defined values files to override the default
                    chart values.
                  </p>
                  <p>
                    If multiple values files are specified, they will be merged
                    in the order they are specified (last values file takes
                    precedence).
                  </p>
                  <p>
                    You can get more information about the Helm values file
                    format in the{' '}
                    <ExternalLink
                      to="https://helm.sh/docs/chart_template_guide/values_files/"
                      data-cy="helm-values-file-format-link"
                    >
                      official documentation
                    </ExternalLink>
                    .
                  </p>
                </>
              }
            >
              <FieldArray name="HelmValuesFiles">
                {({ push, remove }) => (
                  <div>
                    {(values.HelmValuesFiles || []).map((file, index) => (
                      <div key={`values-file-${index}`} className="mb-2">
                        <div className="flex items-start gap-2 align-top">
                          <div className="flex-1">
                            <PathSelector
                              value={file}
                              onChange={(value) =>
                                setFieldValue(`HelmValuesFiles.${index}`, value)
                              }
                              placeholder="e.g. values/production.yaml"
                              model={gitModel}
                              dirOnly={false} // Show files for values files
                              inputId={`helm-values-file-${index}`}
                              hasError={!!errors.HelmValuesFiles?.[index]}
                            />
                          </div>
                          <Button
                            type="button"
                            color="dangerlight"
                            className="h-[34px]"
                            data-cy={`helm-values-file-remove-${index}`}
                            onClick={() => remove(index)}
                            icon={Trash2}
                            size="medium"
                          >
                            <span className="sr-only">Remove</span>
                          </Button>
                        </div>
                        {errors.HelmValuesFiles?.[index] && (
                          <FormError>
                            {errors.HelmValuesFiles?.[index]}
                          </FormError>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      color="secondary"
                      size="small"
                      onClick={() => push('')}
                      data-cy="helm-values-file-add"
                      icon={Plus}
                      className="!ml-0"
                    >
                      Add values file
                    </Button>
                  </div>
                )}
              </FieldArray>
            </FormControl>
            {gitHelmChartValuesQuery.isError && (
              <TextTip color="orange" className="mb-3">
                Unable to load chart default values. The chart may not have a
                values.yaml file, or the chart path may be incorrect.
              </TextTip>
            )}
            {helmValuesPreviewQuery.isError && (
              <TextTip color="orange" className="mb-3">
                Unable to load user-defined values from the specified files.
                Please verify the file paths are correct and the files contain
                valid YAML.
              </TextTip>
            )}
            <HelmValuesInput
              values={helmValuesPreviewQuery.data?.mergedValues ?? ''}
              setValues={() => {}} // No-op, read-only
              valuesRef={gitHelmChartValuesQuery.data?.values ?? ''}
              isValuesRefLoading={gitHelmChartValuesQuery.isInitialLoading}
              isUserDefinedLoading={helmValuesPreviewQuery.isInitialLoading}
              isUserDefinedReadOnly
              userDefinedLabel="User-defined values from Git (read-only)"
              userDefinedTooltip="These are the merged values from the values files specified in your Git repository. They are read-only and will be used during installation."
              userDefinedPlaceholder={`No user-defined values found${
                helmValuesPreviewQuery.isError
                  ? ' - check your values file paths and try again'
                  : ''
              }`}
            />
          </FormSection>
          <div className="mb-8">
            <ManifestPreviewFormSection
              mode="git"
              payload={previewPayload}
              onChangePreviewValidation={setPreviewIsValid}
              title="Manifest preview"
              environmentId={environmentId}
            />
          </div>
          <LoadingButton
            type="submit"
            className="!ml-0"
            loadingText="Installing Helm chart..."
            isLoading={isSubmitting}
            disabled={!isValid}
            data-cy="k8sAppInstall-installButton"
          >
            Install
          </LoadingButton>
        </>
      )}
    </div>
  );

  function handleSourceChange(source?: Source | null) {
    setValues((prev) => ({
      ...prev,
      SourceId: source?.id ?? undefined,
      RepositoryURL: source?.url ?? '',
    }));
  }
}
